import { useCallback, useEffect, useRef, useState } from 'react';

import { TypewriterOnce } from '../components/TypewriterOnce';

const WEBSITE_MESSAGES = [
	"I'm scanning your website",
	"I'm reading your hero section",
	"I'm breaking down your offer",
	"I'm reviewing your messaging",
	"I'm evaluating your pricing",
];

const IG_MESSAGES = [
	"I'm scrolling through your IG content",
	"I'm reviewing your hashtag strategy",
	"I'm measuring your engagement",
	"I'm checking your posting cadence",
	"I'm spotting your visual hooks",
];

const STEPS_PER_PHASE = 5;
const STEP_DURATION_MS = 5000;

type AnalysisScreenProps = {
	screenshotUrl?: string;
	igThumbnails?: string[];
	onDone: () => void;
};

export const AnalysisScreen = ({
	screenshotUrl,
	igThumbnails,
	onDone,
}: AnalysisScreenProps) => {
	// Only animate phases for assets we actually have. When the website
	// screenshot failed/was skipped (no screenshotUrl), drop the website phase
	// entirely and go straight to the Instagram previews instead of scanning an
	// empty browser frame.
	const hasWebsite = !!screenshotUrl;
	const hasIg = Array.isArray(igThumbnails) && igThumbnails.length > 0;
	const phases: ('website' | 'ig')[] = [
		...(hasWebsite ? (['website'] as const) : []),
		...(hasIg ? (['ig'] as const) : []),
	];
	const totalSteps = phases.length * STEPS_PER_PHASE;

	const [step, setStep] = useState(0);
	const doneFired = useRef(false);

	// IG staggered loading: the URL each slot shows, the URL each slot last
	// preloaded, and the time the IG phase started.
	const [shownIgUrls, setShownIgUrls] = useState<(string | undefined)[]>([]);
	const requestedIgUrls = useRef<(string | undefined)[]>([]);
	const igStartedAt = useRef(0);

	// Website scroll animation state
	const [scrollY, setScrollY] = useState(0);
	const imgRef = useRef<HTMLImageElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const [imgH, setImgH] = useState(0);
	const [vpH, setVpH] = useState(0);
	const [scrollReady, setScrollReady] = useState(false);

	// Derived state from step
	const phaseIdx = Math.floor(step / STEPS_PER_PHASE);
	const showing = phases[phaseIdx] ?? 'ig';
	const messageIdx = step % STEPS_PER_PHASE;
	const messages = showing === 'ig' ? IG_MESSAGES : WEBSITE_MESSAGES;

	const fireDone = () => {
		if (!doneFired.current) {
			doneFired.current = true;
			onDone();
		}
	};

	// Step timer — advance every 5s, then fireDone after last step. If there are
	// no phases to show (no assets), finish immediately.
	useEffect(() => {
		if (totalSteps === 0) {
			fireDone();
			return;
		}
		const t = setTimeout(() => {
			if (step < totalSteps - 1) {
				setStep((prev) => prev + 1);
			} else {
				fireDone();
			}
		}, STEP_DURATION_MS);
		return () => clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [step, totalSteps]);

	// Measure image dimensions on load for scroll animation
	const handleImgLoad = useCallback(() => {
		if (imgRef.current && containerRef.current) {
			const cw = containerRef.current.offsetWidth;
			const scale = cw / imgRef.current.naturalWidth;
			setImgH(imgRef.current.naturalHeight * scale);
			setVpH(containerRef.current.offsetHeight);
			setScrollReady(true);
		}
	}, []);

	// Fallback: if image loaded from cache before ref attached, re-measure
	useEffect(() => {
		if (!screenshotUrl || scrollReady || showing !== 'website') return;
		const t = setTimeout(() => {
			if (imgRef.current && containerRef.current) {
				handleImgLoad();
			}
		}, 500);
		return () => clearTimeout(t);
	}, [screenshotUrl, scrollReady, showing, handleImgLoad]);

	// Velocity-based scroll animation for website screenshot
	useEffect(() => {
		if (!scrollReady || showing !== 'website') return;
		const max = Math.max(0, imgH - vpH);
		if (max <= 0) return;

		let pos = 0;
		let velocity = 0;
		let paused = false;
		const burstTargets = [
			max * 0.25,
			max * 0.5,
			max * 0.8,
			max * 0.4,
			max * 0.15,
			max * 0.65,
			max * 0.95,
			max * 0.3,
			0,
			max * 0.5,
		];
		let burstIdx = 0;
		let target = 0;

		const pickNext = () => {
			target = burstTargets[burstIdx % burstTargets.length];
			burstIdx++;
			paused = true;
			setTimeout(
				() => {
					paused = false;
				},
				400 + Math.random() * 600,
			);
		};
		pickNext();

		const interval = setInterval(() => {
			if (paused) {
				setScrollY(pos);
				return;
			}
			const diff = target - pos;
			if (Math.abs(diff) < 2) {
				pickNext();
				return;
			}
			velocity = diff * 0.08;
			pos += velocity;
			pos = Math.max(0, Math.min(max, pos));
			setScrollY(pos);
		}, 16);

		return () => clearInterval(interval);
	}, [scrollReady, imgH, vpH, showing]);

	// Reset scroll state when switching to IG
	useEffect(() => {
		if (showing !== 'website') {
			setScrollY(0);
			setScrollReady(false);
		}
	}, [showing]);

	// The IG phase starts with every slot empty, and the 2s stagger counts from
	// its start.
	useEffect(() => {
		setShownIgUrls([]);
		requestedIgUrls.current = [];
		igStartedAt.current = Date.now();
	}, [showing]);

	// IG stagger effect — preload one image every 2s of the IG phase, and show
	// it only once it decodes. The visible <img> then paints from cache, and a
	// failed URL never flashes a broken-image icon. A new list can replace a
	// stored URL whose access signature expired with a fresh one. The slot keeps
	// its loaded image until the new URL loads, and keeps it for good if the new
	// URL fails. A slot that is already due preloads its new URL at once.
	const igKey = igThumbnails?.join('\n');
	useEffect(() => {
		if (showing !== 'ig') {
			return;
		}
		const timers: ReturnType<typeof setTimeout>[] = [];
		for (let i = 0; i < 9; i++) {
			const url = igThumbnails?.[i];
			if (!url) continue;
			const dueAt = igStartedAt.current + i * 2000;
			timers.push(
				setTimeout(
					() => {
						if (requestedIgUrls.current[i] === url) return;
						requestedIgUrls.current[i] = url;
						const img = new Image();
						img.onload = () => {
							if (requestedIgUrls.current[i] !== url) return;
							setShownIgUrls((prev) => {
								if (prev[i] === url) return prev;
								const next = [...prev];
								next[i] = url;
								return next;
							});
						};
						img.src = url;
					},
					Math.max(0, dueAt - Date.now()),
				),
			);
		}
		return () => timers.forEach(clearTimeout);
		// `igKey` stands for `igThumbnails`: bridge state gives a new array on
		// each delta.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showing, igKey]);

	return (
		<div className="flex h-full w-full">
			{/* Main content */}
			<div className="flex w-full flex-col items-center px-8">
				{/* Top spacer — pushes text to ~15% from top */}
				<div className="flex-[15]" />
				<h1
					className="mb-3 text-center"
					style={{
						fontSize: 26,
						fontWeight: 700,
						letterSpacing: '-0.02em',
						color: 'var(--text-primary)',
						minHeight: 68,
					}}
				>
					<TypewriterOnce key={step} text={messages[messageIdx]} speed={35} />
				</h1>

				{/* Content area — flex-[65] fills remaining space; both views share same container height so text never shifts */}
				<div
					className="flex min-h-[200px] w-full flex-[85] justify-center
						overflow-hidden pb-8"
				>
					{/* Website: browser viewport frame */}
					{showing === 'website' && (
						<div
							className="flex w-full flex-col overflow-hidden"
							style={{
								maxWidth: 900,
								maxHeight: '100%',
								aspectRatio: '16/10',
								borderRadius: 12,
								border: '1px solid #E5E7EB',
								background: 'white',
							}}
						>
							{/* Browser chrome */}
							<div
								className="flex items-center gap-1.5 px-3"
								style={{
									height: 32,
									borderBottom: '1px solid #E5E7EB',
									background: '#FAFAFA',
								}}
							>
								<div
									className="rounded-full"
									style={{ width: 8, height: 8, background: '#E5E7EB' }}
								/>
								<div
									className="rounded-full"
									style={{ width: 8, height: 8, background: '#E5E7EB' }}
								/>
								<div
									className="rounded-full"
									style={{ width: 8, height: 8, background: '#E5E7EB' }}
								/>
							</div>

							{/* Content area */}
							<div
								ref={containerRef}
								className="relative w-full flex-1 overflow-hidden"
							>
								{screenshotUrl && (
									<div
										className="animate-onb-fade-in relative h-full w-full
											overflow-hidden"
									>
										<div
											className="animate-onb-shimmer pointer-events-none
												absolute inset-0 z-10"
											style={{
												background:
													'linear-gradient(105deg, transparent 40%, rgba(37,99,235,0.03) 45%, rgba(37,99,235,0.06) 50%, rgba(37,99,235,0.03) 55%, transparent 60%)',
												backgroundSize: '200% 100%',
											}}
										/>
										<img
											ref={imgRef}
											src={screenshotUrl}
											alt="Website screenshot"
											onLoad={handleImgLoad}
											className="w-full"
											style={{
												transform: `translateY(-${scrollY}px)`,
												willChange: 'transform',
											}}
										/>
									</div>
								)}
							</div>
						</div>
					)}

					{/* Instagram: standalone masonry grid */}
					{showing === 'ig' && igThumbnails?.length && (
						<div
							className="animate-onb-fade-in w-full overflow-hidden"
							style={{
								maxWidth: 500,
								maxHeight: '100%',
								columns: 3,
								columnGap: 8,
							}}
						>
							{Array.from({ length: 9 }).map((_, i) => {
								const shownUrl = shownIgUrls[i];
								const hasImg = i < (igThumbnails?.length ?? 0);
								return (
									<div
										key={i}
										className="overflow-hidden"
										style={{
											breakInside: 'avoid',
											marginBottom: 8,
											borderRadius: 10,
										}}
									>
										{shownUrl && hasImg ? (
											<img
												src={shownUrl}
												alt={`Instagram post ${i + 1}`}
												style={{
													width: '100%',
													aspectRatio: '1/1',
													objectFit: 'cover',
													display: 'block',
													borderRadius: 10,
													animation: 'onb-fade-in 0.5s ease',
												}}
											/>
										) : (
											<div
												className="relative"
												style={{
													paddingBottom: '100%',
													background: '#EBEBEB',
													borderRadius: 10,
												}}
											>
												<div
													className="animate-onb-shimmer absolute inset-0"
													style={{
														background:
															'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.5) 50%, transparent 60%)',
														backgroundSize: '200% 100%',
														borderRadius: 10,
													}}
												/>
											</div>
										)}
									</div>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
